
import React from 'react';
import { format } from 'date-fns';
import { Review } from '@/types/review';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { ReviewStars } from '@/components/admin/reviews/ReviewStars';
import { ReviewActions } from '@/components/admin/reviews/ReviewActions';
import { Badge } from '@/components/ui/badge';
import { useIsMobile } from '@/hooks/use-mobile';

interface ReviewsTableProps {
  reviews: Review[];
  isLoading: boolean;
}

export function ReviewsTable({ reviews, isLoading }: ReviewsTableProps) {
  const isMobile = useIsMobile();
  
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>User</TableHead>
            <TableHead>Rating</TableHead>
            {!isMobile && <TableHead>Title</TableHead>}
            {!isMobile && <TableHead>Content</TableHead>}
            <TableHead>{isMobile ? "Course" : "Course Name"}</TableHead>
            {!isMobile && <TableHead>Date</TableHead>}
            <TableHead className="text-center">Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={isMobile ? 5 : 8} className="h-24 text-center">
                Loading reviews...
              </TableCell>
            </TableRow>
          ) : reviews && reviews.length > 0 ? (
            reviews.map((review) => (
              <TableRow key={review.id}>
                <TableCell className="font-medium p-2 md:p-4">
                  <div className="flex flex-col">
                    <span>{review.user_name || review.custom_user_name || 'Anonymous'}</span>
                    {review.custom_user_name && <small className="text-muted-foreground">(custom)</small>}
                    {isMobile && (
                      <small className="text-xs text-muted-foreground">
                        {review.created_at ? format(new Date(review.created_at), 'MMM d') : '-'}
                      </small>
                    )}
                  </div>
                </TableCell>
                <TableCell className="p-2 md:p-4">
                  <ReviewStars rating={review.rating} />
                </TableCell>
                {!isMobile && (
                  <TableCell>
                    {review.title || <span className="text-muted-foreground italic">No title</span>}
                  </TableCell>
                )}
                {!isMobile && (
                  <TableCell className="max-w-xs truncate">
                    {review.content || <span className="text-muted-foreground italic">No content</span>}
                  </TableCell>
                )}
                <TableCell className="p-2 md:p-4">
                  {review.course_name || <span className="text-muted-foreground italic">Unknown</span>}
                </TableCell>
                {!isMobile && (
                  <TableCell>
                    {review.created_at ? format(new Date(review.created_at), 'MMM d, yyyy') : '-'}
                  </TableCell>
                )}
                <TableCell className="p-2 md:p-4">
                  <div className="flex flex-col items-center gap-1">
                    {review.is_approved ? 
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">Approved</Badge> : 
                      <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200 text-xs">Pending</Badge>
                    }
                    {isMobile ? null : (
                      <>
                        {review.is_featured && 
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">Featured</Badge>
                        }
                        {review.is_flagged && 
                          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-xs">Flagged</Badge>
                        }
                      </>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right p-2 md:p-4">
                  <ReviewActions review={review} />
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={isMobile ? 5 : 8} className="h-24 text-center">
                No reviews found.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
